// src/data/continuityPlan.js
//
// Track 6 item 6a-11 (2026-09-14, task list section 229). Ruling 18, restated in data\YCP_LC_GC_
// WORKSPACE_REQUIREMENTS_2026-08-23.md SS6.4: every General Chairperson should have a continuity plan,
// OFFERED four ways - adopt this one, write your own, borrow another committee's, or have none.
// "The object is a template plus an adoption record. `none` is a valid recorded state: the register
// must be able to show this committee considered it and declined, which is not the same as silence.
// Fourteen General Committees are autonomous; the platform offers, it does not impose."
//
// The template is data\YCP_GC_CONTINUITY_PLAN_2026-08-21.md (a DRAFT in Bob's words at its head; it
// becomes adopted by a committee's record here, never by this file). The adoption record lives on
// committees/{committee}.continuityPlan, written by the General Chairperson of that committee and by
// nobody else (firestore.rules: the only field a GC may touch on his committee document). Pure module.

export const TEMPLATE = { id: 'YCP_GC_CONTINUITY_PLAN_2026-08-21', title: 'General Chairperson - Continuity of Office Plan (21 August 2026)' };

export const CHOICES = [
  { key: 'adopted',  label: 'Adopt this plan',            says: 'This committee adopts the platform\'s continuity plan as its own.' },
  { key: 'own',      label: 'Write our own',              says: 'This committee has, or will write, a continuity plan of its own.' },
  { key: 'borrowed', label: 'Borrow another committee\'s', says: 'This committee follows another General Committee\'s plan.' },
  { key: 'declined', label: 'Have none',                  says: 'This committee considered a continuity plan and declined one. That is a decision on record, not silence.' },
];
export const CHOICE_KEYS = CHOICES.map(c => c.key);
export const NOTE_MAX = 400;

export function mayRecord(user, committee) {
  if (!user || !committee) return false;
  if (user.isAdmin) return true;
  return user.role === 'general_chair' && user.committee === committee;
}

// The record a GC writes. Returns { ok, errors, patch } - the patch is { continuityPlan: {...} } and
// nothing else, so the rule that lets a GC touch ONLY that field is the same line as this shape.
export function continuityRecord(user, committee, input) {
  const i = input || {};
  const errors = [];
  if (!mayRecord(user, committee)) errors.push('Only the General Chairperson of ' + (committee || 'this committee') + ' records its choice.');
  const choice = String(i.choice || '').trim();
  if (!CHOICE_KEYS.includes(choice)) errors.push('Choose one of the four: ' + CHOICES.map(c => c.label).join(' / ') + '.');
  const borrowedFrom = String(i.borrowedFrom || '').trim();
  if (choice === 'borrowed') {
    if (!/^GO-[A-Z0-9]+$/.test(borrowedFrom)) errors.push('Name the committee whose plan is followed (GO-nnn).');
    else if (borrowedFrom === committee) errors.push('A committee cannot borrow its own plan.');
  }
  const note = String(i.note || '').trim();
  if (note.length > NOTE_MAX) errors.push('The note is limited to ' + NOTE_MAX + ' characters.');
  if (choice === 'own' && !note) errors.push('Say where the committee\'s own plan is kept, or that it is still to be written.');
  if (errors.length) return { ok: false, errors, patch: null };
  return {
    ok: true, errors: [],
    patch: {
      continuityPlan: {
        choice,
        template: TEMPLATE.id,
        borrowedFrom: choice === 'borrowed' ? borrowedFrom : null,
        note: note || null,
        recordedBy: user.uid || null,
        recordedByName: user.name || user.email || 'Unknown',
        recordedByRole: user.isAdmin ? 'admin' : 'general_chair',
        recordedAt: i.at || new Date().toISOString(),
      },
    },
  };
}

// What a screen or the register prints. An absent record is "not yet considered" - and that is the
// state SS6.4 distinguishes from "declined".
export function continuityLine(record) {
  if (!record || !record.choice) return 'No continuity plan choice is on record for this committee - not adopted, not declined, not yet considered.';
  const c = CHOICES.find(x => x.key === record.choice);
  const who = (record.recordedByName || 'Unknown') + ' on ' + String(record.recordedAt || '').slice(0, 10);
  const base = (c ? c.says : 'Choice ' + record.choice + '.') + ' Recorded by ' + who + '.';
  if (record.choice === 'borrowed' && record.borrowedFrom) return base + ' Plan followed: ' + record.borrowedFrom + '.';
  if (record.note) return base + ' Note: ' + record.note;
  return base;
}
